import {AccessCodeAlphabet, Box, Occupancy, OccupyRequest} from "@variocube/app-api";
import * as React from "react";
import {useCallback, useMemo, useState} from "react";
import {auditLog} from "./auditLog";
import {CarrierSelectionView} from "./CarrierSelectionView";
import {Account, AccountSettings, Carrier, Delivery, RecipientSelection} from "./model";
import {RecipientSelectionView} from "./RecipientSelectionView";
import {BoxSelectionView} from "./BoxSelectionView";
import {StorageView} from "./StorageView";
import {SuccessView} from "./SuccessView";
import {BoxTypeSelectionResult, usePlatform, usePreventAutoDeactivate, useStorageItemValue} from "@variocube/cube-ui";
import {logger} from "./logger";
import {TrackingNumberInputView} from "./TrackingNumberInputView";
import {useLocalization} from "../i18n";

const log = logger.sub("DeliveryFlow");

enum inputTrackingNumberEnum {
    required = "required",
    optional = "optional",
    disabled = "disabled"
}

interface DeliveryFlowProps {
    carriers: Carrier[];
    sizes: string[];
    onExit: () => void;
}

export function DeliveryFlow({carriers, sizes, onExit}: DeliveryFlowProps) {
    const platform = usePlatform();
    const {t} = useLocalization();
    const accountSettings = useStorageItemValue<AccountSettings>("accountSettings");
    const recipientSelection = useStorageItemValue<RecipientSelection>("recipientSelection");
    const accounts = useStorageItemValue<Account[]>("accounts");
    const poBoxUrl = useStorageItemValue<string>("poBoxUrl");
    const inputTrackingNumber = useStorageItemValue<string>("inputTrackingNumber");

    const isSingleCarrier = carriers.length == 1;
    const [carrier, setCarrier] = useState<Carrier | undefined>(isSingleCarrier ? carriers[0] : undefined);
    const [recipient, setRecipient] = useState<Account>();
    const [boxSelection, setBoxSelection] = useState<BoxTypeSelectionResult>();
    const [boxNumber, setBoxNumber] = useState<string>();
    const [success, setSuccess] = useState<boolean>(false);
    const [trackingNumber, setTrackingNumber] = useState<string>("");
    const [trackingNumberCompleted, setTrackingNumberCompleted] = useState<boolean>(false);

    const displayStepInputTrackingNumber = inputTrackingNumber !== inputTrackingNumberEnum.disabled.toString();

    const handleRecipientSelectionBack = useCallback(() => {
        if(displayStepInputTrackingNumber){
            setTrackingNumberCompleted(false);
        }else if(isSingleCarrier){
            onExit()
        }else{
            setCarrier(undefined);
        }
    }, [displayStepInputTrackingNumber, isSingleCarrier, onExit]);

    const handleTrackingNumberInputBack = useCallback( () => {
        if(isSingleCarrier) {
            onExit();
        }else{
            setCarrier(undefined);
        }
    }, [isSingleCarrier, onExit]);

    const handleSetTrackingNumber = useCallback( (trackingNumber: string) => {
        setTrackingNumber(trackingNumber);
        setTrackingNumberCompleted(true);
    }, []);

    const handleSizeBack = useCallback(() => setRecipient(undefined), []);

    const storageRequest = useMemo((): OccupyRequest|undefined => {
        if ((boxSelection || boxNumber) && recipient && carrier) {
            const delivery: Delivery = {
                carrier,
                postBoxId: recipient.postBoxId,
                trackingNumber: trackingNumber,
                // and for legacy compatibility:
                deliverer: carrier.name,
                sender: "",
            };

            return {
                ...boxSelection,
                boxNumber,
                accessCodeShape: {
                    alphabet: AccessCodeAlphabet.OneToNine,
                    length: 6,
                },
                content: {
                    delivery
                }
            }
        }
    }, [boxSelection, boxNumber, recipient, carrier]);

    const handleStorageWrongSize = useCallback(() => {
        log.info("User selected wrong size");
        setBoxSelection(undefined);
        setBoxNumber(undefined);
    }, []);

    const fixedPickupCode = useMemo(() => recipient && accountSettings && accountSettings[recipient.postBoxId]?.fixedPickupCode, [recipient, accountSettings]);

    const handleStoragePreConfirm = useCallback(async (occupancy: Occupancy) => {
        if (fixedPickupCode) {
            log.info("Setting fixed pickup code", {fixedPickupCode, occupancy})
            await platform.changeAccessCode(occupancy.uuid, fixedPickupCode);
        }
    }, [platform, fixedPickupCode]);

    const handleStorageConfirm = useCallback(async (occupancy: Occupancy) => {
        await auditLog(platform, "Store", {
            occupancy,
            box: occupancy.boxNumber,
            delivery: {boxSelection, recipient, carrier}
        });
        setSuccess(true);
    }, [platform, boxSelection, recipient, carrier, fixedPickupCode]);

    if (!carrier) {
        return (
            <CarrierSelectionView
                carriers={carriers}
                onSelect={setCarrier}
                onBack={onExit}
            />
        );
    }

    if(displayStepInputTrackingNumber && !trackingNumberCompleted) {
        return (
            <TrackingNumberInputView
                onBack={handleTrackingNumberInputBack}
                onSelect={handleSetTrackingNumber}
                required={inputTrackingNumber === inputTrackingNumberEnum.required.toString()}
            />
        );
    }

    if (!recipient) {
        return (
            <RecipientSelectionView
                onBack={handleRecipientSelectionBack}
                onSelect={setRecipient}
                recipientSelection={recipientSelection || RecipientSelection.Search}
                accounts={accounts ?? []}
                poBoxUrl={poBoxUrl}
            />
        );
    }

    if (!boxSelection && !boxNumber) {
        return (
            <BoxSelectionView
                sizes={sizes}
                onTypeSelect={setBoxSelection}
                onNumberSelect={setBoxNumber}
                onBack={handleSizeBack}
                recipient={recipient}
            />
        );
    }

    if (!success) {
        if (!storageRequest) {
            log.error("Invalid state: storageRequest is undefined.");
            return null;
        }
        return (
            <StorageView
                request={storageRequest}
                onWrongSize={handleStorageWrongSize}
                onCancel={onExit}
                onPreConfirm={handleStoragePreConfirm}
                onConfirm={handleStorageConfirm}
                boxOpenTitle={"DeliveryStoreTitle"}
                boxOpenMessage={"DeliveryStoreMessage"}
                confirmTitle={"DeliveryConfirmTitle"}
                confirmMessage={"DeliveryConfirmMessage"}
            />
        );
    }

    return (
        <SuccessView
            title={t("DeliverySuccessTitle")}
            message={t("DeliverySuccessMessage")}
            onClose={onExit}
        />
    );
}
